/**
 * Country-Specific Knowledge Bases for Professional Domains
 * 
 * Enterprise-level professional knowledge for:
 * - Accounting Standards
 * - Auditing Standards
 * - Taxation Laws
 * - Business Laws
 * - Management Advisory Services
 * 
 * Supports multiple jurisdictions with accurate, up-to-date information.
 */

export interface CountryKnowledgeBase {
    country: string;
    countryCode: string;
    currency: {
        code: string;
        symbol: string;
        name: string;
    };
    
    // Accounting Standards
    accounting: {
        framework: string;
        adoptedStandards: string[];
        regulatoryBody: string;
        keyStandards: {
            revenue: string;
            leases: string;
            financialInstruments: string;
            inventories: string;
            ppe: string;
            impairment: string;
            consolidation: string;
        };
        localVariations: string[];
    };
    
    // Auditing Standards
    auditing: {
        framework: string;
        regulatoryBody: string;
        keyStandards: string[];
        reportingRequirements: string[];
        materialityGuidance: string;
    };
    
    // Taxation
    taxation: {
        authority: string;
        corporateIncomeTax: {
            standardRate: string;
            reducedRates: { condition: string; rate: string }[];
            fiscalYear: string;
        };
        individualIncomeTax: {
            brackets: { from: number; to: number | null; rate: string }[];
            exemptions: string[];
        };
        valueAddedTax: {
            standardRate: string;
            reducedRates: { category: string; rate: string }[];
            registrationThreshold: string;
        };
        withholdingTax: {
            dividends: string;
            interest: string;
            royalties: string;
            services: string;
        };
        keyLaws: string[];
        filingDeadlines: string[];
        recentReforms: string[];
    };
    
    // Business Law
    businessLaw: {
        framework: string;
        corporateTypes: string[];
        regulatoryBodies: string[];
        keyLegislation: string[];
        contractLaw: string;
        employmentLaw: {
            minimumWage: string;
            workingHours: string;
            benefits: string[];
        };
    };
    
    // Management Advisory
    managementAdvisory: {
        localPractices: string[];
        industryStandards: string[];
        commonMetrics: string[];
    };
    
    // System Prompt Enhancement
    systemPromptAddition: string;
}

// ==================== PHILIPPINES ====================
export const PHILIPPINES_KNOWLEDGE: CountryKnowledgeBase = {
    country: 'Philippines',
    countryCode: 'PH',
    currency: {
        code: 'PHP',
        symbol: '₱',
        name: 'Philippine Peso'
    },
    
    accounting: {
        framework: 'Philippine Financial Reporting Standards (PFRS)',
        adoptedStandards: [
            'PFRS 1-17 (IFRS adoptions)',
            'PAS 1-41 (IAS adoptions)',
            'Philippine Interpretations',
            'PFRS for SMEs'
        ],
        regulatoryBody: 'Financial Reporting Standards Council (FRSC) under SEC',
        keyStandards: {
            revenue: 'PFRS 15 - Revenue from Contracts with Customers',
            leases: 'PFRS 16 - Leases',
            financialInstruments: 'PFRS 9 - Financial Instruments',
            inventories: 'PAS 2 - Inventories (LCNRV)',
            ppe: 'PAS 16 - Property, Plant and Equipment',
            impairment: 'PAS 36 - Impairment of Assets',
            consolidation: 'PFRS 10 - Consolidated Financial Statements'
        },
        localVariations: [
            'SEC Memorandum Circulars',
            'BSP Circulars for banks',
            'IC Circulars for insurance',
            'Philippine Interpretations Committee (PIC) Q&As'
        ]
    },
    
    auditing: {
        framework: 'Philippine Standards on Auditing (PSA)',
        regulatoryBody: 'Board of Accountancy (BOA) under PRC',
        keyStandards: [
            'PSA 200 - Overall Objectives',
            'PSA 315 - Identifying and Assessing Risks',
            'PSA 330 - Auditor\'s Responses to Assessed Risks',
            'PSA 500 - Audit Evidence',
            'PSA 700 - Forming an Opinion',
            'PSA 701 - Key Audit Matters',
            'PSA 705 - Modifications to Opinion'
        ],
        reportingRequirements: [
            'Annual Audited Financial Statements',
            'SEC Form 17-A (Listed Companies)',
            'GIS (General Information Sheet)',
            'ITR Attachments'
        ],
        materialityGuidance: 'Generally 0.5-5% of appropriate benchmark (revenue, total assets, or profit before tax)'
    },
    
    taxation: {
        authority: 'Bureau of Internal Revenue (BIR)',
        corporateIncomeTax: {
            standardRate: '25% Regular Corporate Income Tax (RCIT)',
            reducedRates: [
                { condition: 'MSME (Net TI ≤ ₱5M AND Total Assets ≤ ₱100M, excl. land)', rate: '20%' },
                { condition: 'Proprietary Educational Institutions & Hospitals (if GI from unrelated activities ≤ 50%)', rate: '10%' },
                { condition: 'PEZA/BOI Registered (ITH period)', rate: '0%' },
                { condition: 'Special Corporate Income Tax (SCIT) for RBE after ITH', rate: '5% on GIE or 5% GIT' }
            ],
            fiscalYear: 'Calendar Year (Jan-Dec) standard; Fiscal year with BIR approval'
        },
        individualIncomeTax: {
            brackets: [
                { from: 0, to: 250000, rate: '0%' },
                { from: 250001, to: 400000, rate: '15% of excess over ₱250,000' },
                { from: 400001, to: 800000, rate: '₱22,500 + 20% of excess over ₱400,000' },
                { from: 800001, to: 2000000, rate: '₱102,500 + 25% of excess over ₱800,000' },
                { from: 2000001, to: 8000000, rate: '₱402,500 + 30% of excess over ₱2,000,000' },
                { from: 8000001, to: null, rate: '₱2,202,500 + 35% of excess over ₱8,000,000' }
            ],
            exemptions: [
                '₱250,000 annual threshold',
                '13th month pay and other benefits (≤ ₱90,000)',
                'SSS/GSIS/PhilHealth/Pag-IBIG contributions',
                'De minimis benefits'
            ]
        },
        valueAddedTax: {
            standardRate: '12%',
            reducedRates: [
                { category: 'VAT-Exempt (Basic necessities, agricultural)', rate: '0%' },
                { category: 'Zero-Rated (Exports, PEZA sales)', rate: '0%' },
                { category: 'Percentage Tax (Non-VAT)', rate: '1-3%' }
            ],
            registrationThreshold: '₱3,000,000 annual gross sales/receipts'
        },
        withholdingTax: {
            dividends: '10% final tax (cash or property)',
            interest: '20% final tax (bank deposits)',
            royalties: '20% final tax',
            services: '1-15% expanded withholding tax (varies by service)'
        },
        keyLaws: [
            'National Internal Revenue Code (NIRC) of 1997, as amended',
            'CREATE Act (RA 11534) - Corporate Recovery and Tax Incentives for Enterprises',
            'TRAIN Law (RA 10963) - Tax Reform for Acceleration and Inclusion',
            'EOPT Act (RA 11976) - Ease of Paying Taxes',
            'Revenue Regulations and Revenue Memorandum Circulars'
        ],
        filingDeadlines: [
            'Annual ITR: April 15',
            'Quarterly ITR: 60 days after quarter-end',
            'Monthly VAT: 25th of following month',
            'Quarterly VAT: 25th of month following quarter',
            'Annual Information Return: January 31'
        ],
        recentReforms: [
            'EOPT Act (2024): Digital filing, invoice system, extended deadlines',
            'CREATE MORE (2024): Enhanced incentives for RBEs',
            'Digital Economy Tax proposals'
        ]
    },
    
    businessLaw: {
        framework: 'Civil Code of the Philippines, Revised Corporation Code, and Special Laws',
        corporateTypes: [
            'Stock Corporation',
            'Non-Stock Corporation',
            'One Person Corporation (OPC)',
            'Partnership (General/Limited)',
            'Sole Proprietorship',
            'Branch Office',
            'Representative Office'
        ],
        regulatoryBodies: [
            'Securities and Exchange Commission (SEC)',
            'Department of Trade and Industry (DTI)',
            'Bangko Sentral ng Pilipinas (BSP)',
            'Insurance Commission (IC)'
        ],
        keyLegislation: [
            'Revised Corporation Code (RA 11232)',
            'Civil Code (RA 386)',
            'Labor Code (PD 442)',
            'Consumer Act (RA 7394)',
            'Intellectual Property Code (RA 8293)',
            'Data Privacy Act (RA 10173)',
            'Anti-Money Laundering Act (RA 9160)',
            'Securities Regulation Code (RA 8799)'
        ],
        contractLaw: 'Civil Code of the Philippines, Articles 1305-1422',
        employmentLaw: {
            minimumWage: '₱610/day (NCR as of 2024) - varies by region',
            workingHours: '8 hours/day, 48 hours/week maximum',
            benefits: [
                '13th Month Pay (mandatory)',
                'Service Incentive Leave (5 days after 1 year)',
                'SSS, PhilHealth, Pag-IBIG contributions',
                'Separation Pay (authorized causes)',
                'Retirement Pay (RA 7641)'
            ]
        }
    },
    
    managementAdvisory: {
        localPractices: [
            'Break-even Analysis for SMEs',
            'Cash Flow Management (OFW remittance cycles)',
            'Working Capital Financing (Bank loans, Trade financing)',
            'BIR compliance optimization'
        ],
        industryStandards: [
            'BSP regulations for banking sector',
            'Insurance Commission circulars',
            'PSE listing requirements',
            'PEZA/BOI registration for manufacturing'
        ],
        commonMetrics: [
            'Current Ratio (2:1 typical for banks)',
            'Debt-to-Equity (varies by industry)',
            'Return on Assets/Equity',
            'Days Sales Outstanding'
        ]
    },
    
    systemPromptAddition: `
### 🇵🇭 PHILIPPINE JURISDICTION CONTEXT

**ACCOUNTING:**
- Framework: PFRS/PAS (Philippine adoption of IFRS)
- Cite specific paragraph numbers: "PAS 16.30-35"
- Reference SEC Memorandum Circulars when relevant
- For SMEs, consider PFRS for SMEs applicability

**AUDITING:**
- Standards: Philippine Standards on Auditing (PSA)
- Cite PSA numbers: "PSA 315.12"
- Consider SEC requirements for listed companies
- BOA regulations for CPAs

**TAXATION (CRITICAL - Always specify law version):**
- CREATE Act (RA 11534) effective April 11, 2021:
  - RCIT: 25% regular, 20% for MSMEs
  - MCIT: 2% (reduced from 2%)
  - Proprietary hospitals/schools: 10%
- EOPT Act (RA 11976) effective January 22, 2024:
  - Invoice System for VAT
  - Extended filing deadlines
  - Digital filing mandates
- TRAIN Law rates for individuals
- Always state if pre-CREATE, CREATE, or EOPT applies

**BUSINESS LAW:**
- Revised Corporation Code (RA 11232) - effective March 2019
- Civil Code for contracts
- Labor Code for employment matters

**CURRENCY:** Always use ₱ (Philippine Peso)
**EXAMPLES:** Use Filipino business names and scenarios
`
};

// ==================== UNITED STATES ====================
export const USA_KNOWLEDGE: CountryKnowledgeBase = {
    country: 'United States',
    countryCode: 'US',
    currency: {
        code: 'USD',
        symbol: '$',
        name: 'US Dollar'
    },
    
    accounting: {
        framework: 'US Generally Accepted Accounting Principles (US GAAP)',
        adoptedStandards: [
            'FASB Accounting Standards Codification (ASC)',
            'SEC Regulations (S-X, S-K)',
            'AICPA Standards'
        ],
        regulatoryBody: 'Financial Accounting Standards Board (FASB)',
        keyStandards: {
            revenue: 'ASC 606 - Revenue from Contracts with Customers',
            leases: 'ASC 842 - Leases',
            financialInstruments: 'ASC 815/320/325 - Derivatives and Investments',
            inventories: 'ASC 330 - Inventory (LCM or LCNRV)',
            ppe: 'ASC 360 - Property, Plant, and Equipment',
            impairment: 'ASC 350/360 - Impairment',
            consolidation: 'ASC 810 - Consolidation'
        },
        localVariations: [
            'SEC Staff Accounting Bulletins (SABs)',
            'EITF Issues',
            'PCAOB Standards for public companies',
            'AICPA Audit and Accounting Guides'
        ]
    },
    
    auditing: {
        framework: 'Generally Accepted Auditing Standards (GAAS) / PCAOB Standards',
        regulatoryBody: 'PCAOB (Public Companies), AICPA (Private)',
        keyStandards: [
            'AU-C 200 - General Principles',
            'AU-C 315 - Understanding the Entity',
            'AU-C 500 - Audit Evidence',
            'AU-C 700 - Audit Opinion',
            'AS 2201 - Internal Control Audit (PCAOB)',
            'AS 3101 - Auditor\'s Report (PCAOB)'
        ],
        reportingRequirements: [
            'Form 10-K (Annual Report)',
            'Form 10-Q (Quarterly Report)',
            'Form 8-K (Current Report)',
            'SOX 404 Internal Control Report'
        ],
        materialityGuidance: '5% of pre-tax income commonly used; 0.5-1% of revenue for larger companies'
    },
    
    taxation: {
        authority: 'Internal Revenue Service (IRS)',
        corporateIncomeTax: {
            standardRate: '21% flat federal corporate tax',
            reducedRates: [
                { condition: 'Qualified Small Business Stock (QSBS)', rate: '0% on capital gains (up to limits)' },
                { condition: 'Qualified Opportunity Zone investments', rate: 'Deferred/reduced capital gains' }
            ],
            fiscalYear: 'Calendar or fiscal year (with IRS election)'
        },
        individualIncomeTax: {
            brackets: [
                { from: 0, to: 11600, rate: '10%' },
                { from: 11601, to: 47150, rate: '12%' },
                { from: 47151, to: 100525, rate: '22%' },
                { from: 100526, to: 191950, rate: '24%' },
                { from: 191951, to: 243725, rate: '32%' },
                { from: 243726, to: 609350, rate: '35%' },
                { from: 609351, to: null, rate: '37%' }
            ],
            exemptions: [
                'Standard deduction ($14,600 single, $29,200 MFJ for 2024)',
                'Itemized deductions',
                'Traditional IRA contributions',
                '401(k) contributions',
                'Health Savings Account contributions'
            ]
        },
        valueAddedTax: {
            standardRate: 'No federal VAT/GST',
            reducedRates: [
                { category: 'State Sales Tax', rate: '0-10%+ depending on state' }
            ],
            registrationThreshold: 'Varies by state (economic nexus rules)'
        },
        withholdingTax: {
            dividends: '0-20% (qualified vs ordinary)',
            interest: '0-37% (included in ordinary income)',
            royalties: '30% (non-resident aliens)',
            services: 'No standard withholding (1099 reporting)'
        },
        keyLaws: [
            'Internal Revenue Code (IRC)',
            'Tax Cuts and Jobs Act (TCJA) of 2017',
            'Inflation Reduction Act of 2022',
            'CARES Act (COVID relief)',
            'Treasury Regulations'
        ],
        filingDeadlines: [
            'Individual: April 15 (Form 1040)',
            'C-Corp: April 15 (Form 1120)',
            'S-Corp: March 15 (Form 1120-S)',
            'Partnership: March 15 (Form 1065)',
            'Quarterly Estimated: 15th of 4th, 6th, 9th, and 1st month'
        ],
        recentReforms: [
            'Corporate AMT of 15% (Inflation Reduction Act)',
            'R&D expense amortization requirement',
            'Pillar Two global minimum tax considerations'
        ]
    },
    
    businessLaw: {
        framework: 'Federal and State Laws (varies by state of incorporation)',
        corporateTypes: [
            'C Corporation',
            'S Corporation',
            'Limited Liability Company (LLC)',
            'Partnership (General/Limited/LLP)',
            'Sole Proprietorship',
            'Benefit Corporation (B-Corp)'
        ],
        regulatoryBodies: [
            'Securities and Exchange Commission (SEC)',
            'State Secretary of State offices',
            'Federal Trade Commission (FTC)',
            'Department of Labor (DOL)'
        ],
        keyLegislation: [
            'Delaware General Corporation Law (DGCL)',
            'Securities Act of 1933',
            'Securities Exchange Act of 1934',
            'Sarbanes-Oxley Act (SOX)',
            'Dodd-Frank Act',
            'Uniform Commercial Code (UCC)'
        ],
        contractLaw: 'State law (often UCC for commercial); common law for services',
        employmentLaw: {
            minimumWage: '$7.25/hour federal (varies by state, up to $20+ in some areas)',
            workingHours: '40 hours/week before overtime (FLSA)',
            benefits: [
                'FMLA (12 weeks unpaid family leave)',
                'COBRA (health insurance continuation)',
                'ADA accommodations',
                'Workers\' compensation (state-mandated)',
                'Unemployment insurance'
            ]
        }
    },
    
    managementAdvisory: {
        localPractices: [
            'Activity-Based Costing',
            'Balanced Scorecard',
            'EVA (Economic Value Added)',
            'SOX compliance consulting'
        ],
        industryStandards: [
            'SEC regulations for public companies',
            'PCAOB requirements',
            'COSO Internal Control Framework',
            'NIST Cybersecurity Framework'
        ],
        commonMetrics: [
            'EBITDA',
            'EPS (Earnings Per Share)',
            'Return on Invested Capital (ROIC)',
            'Free Cash Flow'
        ]
    },
    
    systemPromptAddition: `
### 🇺🇸 UNITED STATES JURISDICTION CONTEXT

**ACCOUNTING:**
- Framework: US GAAP (FASB ASC)
- Cite ASC Topic-Subtopic-Section: "ASC 606-10-25"
- For public companies: SEC reporting requirements
- Key differences from IFRS to note when relevant

**AUDITING:**
- Public companies: PCAOB Standards (AS)
- Private companies: AICPA GAAS (AU-C)
- SOX 404 internal control requirements for public companies

**TAXATION:**
- Federal: 21% corporate rate (TCJA)
- State taxes vary (0-12%+ corporate, 0-13%+ individual)
- Self-employment tax: 15.3%
- Capital gains: 0/15/20% + 3.8% NIIT
- Always consider federal AND state implications

**BUSINESS LAW:**
- Delaware law common for corporations
- UCC for commercial transactions
- ERISA for employee benefits

**CURRENCY:** Always use $ (US Dollar)
**EXAMPLES:** Use American business names and scenarios
`
};

// ==================== UNITED KINGDOM ====================
export const UK_KNOWLEDGE: CountryKnowledgeBase = {
    country: 'United Kingdom',
    countryCode: 'GB',
    currency: {
        code: 'GBP',
        symbol: '£',
        name: 'British Pound'
    },
    
    accounting: {
        framework: 'UK-adopted International Accounting Standards / FRS 102',
        adoptedStandards: [
            'UK-adopted IFRS (for listed companies)',
            'FRS 102 (The Financial Reporting Standard)',
            'FRS 101 (Reduced Disclosure Framework)',
            'FRS 105 (Micro-entities)'
        ],
        regulatoryBody: 'Financial Reporting Council (FRC)',
        keyStandards: {
            revenue: 'IFRS 15 / FRS 102 Section 23',
            leases: 'IFRS 16 / FRS 102 Section 20',
            financialInstruments: 'IFRS 9 / FRS 102 Sections 11-12',
            inventories: 'IAS 2 / FRS 102 Section 13',
            ppe: 'IAS 16 / FRS 102 Section 17',
            impairment: 'IAS 36 / FRS 102 Section 27',
            consolidation: 'IFRS 10 / FRS 102 Section 9'
        },
        localVariations: [
            'Companies Act 2006 requirements',
            'FRC guidance and bulletins',
            'Small company exemptions'
        ]
    },
    
    auditing: {
        framework: 'International Standards on Auditing (UK) - ISAs (UK)',
        regulatoryBody: 'Financial Reporting Council (FRC)',
        keyStandards: [
            'ISA (UK) 200 - Overall Objectives',
            'ISA (UK) 315 - Identifying Risks',
            'ISA (UK) 700 - Forming an Opinion',
            'ISA (UK) 720 - Other Information'
        ],
        reportingRequirements: [
            'Annual Accounts filed with Companies House',
            'Audit report for non-exempt companies',
            'Strategic Report for medium/large companies'
        ],
        materialityGuidance: '5% of profit before tax; 0.5-1% of revenue or total assets'
    },
    
    taxation: {
        authority: 'HM Revenue and Customs (HMRC)',
        corporateIncomeTax: {
            standardRate: '25% (profits over £250,000)',
            reducedRates: [
                { condition: 'Profits £50,000 or less', rate: '19%' },
                { condition: 'Profits £50,001-£250,000', rate: 'Marginal relief applies' }
            ],
            fiscalYear: 'Companies choose accounting period'
        },
        individualIncomeTax: {
            brackets: [
                { from: 0, to: 12570, rate: '0% (Personal Allowance)' },
                { from: 12571, to: 50270, rate: '20% (Basic Rate)' },
                { from: 50271, to: 125140, rate: '40% (Higher Rate)' },
                { from: 125141, to: null, rate: '45% (Additional Rate)' }
            ],
            exemptions: [
                'Personal Allowance (£12,570)',
                'ISA allowance (£20,000)',
                'Pension contributions',
                'Marriage Allowance transfer'
            ]
        },
        valueAddedTax: {
            standardRate: '20%',
            reducedRates: [
                { category: 'Domestic fuel/power', rate: '5%' },
                { category: 'Children\'s car seats', rate: '5%' },
                { category: 'Zero-rated (food, books, children\'s clothing)', rate: '0%' },
                { category: 'Exempt (insurance, finance, education)', rate: 'Exempt' }
            ],
            registrationThreshold: '£90,000 (from April 2024)'
        },
        withholdingTax: {
            dividends: '0% (domestic); treaty rates for non-residents',
            interest: '20% (subject to treaty relief)',
            royalties: '20% (subject to treaty relief)',
            services: 'Generally no withholding'
        },
        keyLaws: [
            'Income Tax Act 2007',
            'Corporation Tax Act 2009/2010',
            'Taxation of Chargeable Gains Act 1992',
            'VAT Act 1994',
            'Finance Act (annual updates)'
        ],
        filingDeadlines: [
            'Self Assessment: January 31 (online)',
            'Corporation Tax: 12 months after accounting period end',
            'VAT: Monthly/quarterly depending on scheme'
        ],
        recentReforms: [
            'Corporate tax rate increase to 25% (2023)',
            'Full expensing for plant & machinery',
            'R&D tax relief changes'
        ]
    },
    
    businessLaw: {
        framework: 'Companies Act 2006 and common law',
        corporateTypes: [
            'Private Limited Company (Ltd)',
            'Public Limited Company (PLC)',
            'Limited Liability Partnership (LLP)',
            'Partnership',
            'Sole Trader',
            'Community Interest Company (CIC)'
        ],
        regulatoryBodies: [
            'Companies House',
            'Financial Conduct Authority (FCA)',
            'Prudential Regulation Authority (PRA)'
        ],
        keyLegislation: [
            'Companies Act 2006',
            'Partnership Act 1890',
            'LLP Act 2000',
            'Insolvency Act 1986',
            'UK Corporate Governance Code'
        ],
        contractLaw: 'Common law + Contract (Rights of Third Parties) Act 1999',
        employmentLaw: {
            minimumWage: '£11.44/hour (21+), £8.60 (18-20) from April 2024',
            workingHours: '48 hours/week maximum (opt-out available)',
            benefits: [
                'Statutory Sick Pay (SSP)',
                'Statutory Maternity/Paternity Pay',
                'Pension auto-enrolment',
                '28 days annual leave (including bank holidays)',
                'Notice periods based on service'
            ]
        }
    },
    
    managementAdvisory: {
        localPractices: [
            'Budgeting and forecasting',
            'Transfer pricing documentation',
            'Making Tax Digital (MTD) compliance',
            'BEIS reporting requirements'
        ],
        industryStandards: [
            'FCA regulations for financial services',
            'UK Corporate Governance Code',
            'Stewardship Code'
        ],
        commonMetrics: [
            'EBITDA',
            'Operating margin',
            'Working capital days',
            'Gearing ratio'
        ]
    },
    
    systemPromptAddition: `
### 🇬🇧 UNITED KINGDOM JURISDICTION CONTEXT

**ACCOUNTING:**
- Listed companies: UK-adopted IFRS
- Most others: FRS 102 (UK GAAP)
- Small companies: FRS 102 Section 1A or FRS 105
- Cite FRS 102 Section numbers: "FRS 102.23.4"

**AUDITING:**
- Standards: ISAs (UK)
- Small company audit exemption (turnover < £10.2M, assets < £5.1M, employees < 50)
- FRC quality review

**TAXATION:**
- Corporation Tax: 25% (main rate), 19% (small profits)
- VAT: 20% standard rate
- PAYE for employment income
- Self Assessment for self-employment

**BUSINESS LAW:**
- Companies Act 2006
- Common law contract principles

**CURRENCY:** Always use £ (British Pound)
**EXAMPLES:** Use British business names and scenarios
`
};

// ==================== COUNTRY KNOWLEDGE REGISTRY ====================

export const COUNTRY_KNOWLEDGE_BASES: Record<string, CountryKnowledgeBase> = {
    'Philippines': PHILIPPINES_KNOWLEDGE,
    'United States': USA_KNOWLEDGE,
    'United Kingdom': UK_KNOWLEDGE,
    // Add more countries as needed
};

// ==================== UTILITY FUNCTIONS ====================

/**
 * Get knowledge base for a country
 */
export function getCountryKnowledgeBase(country: string): CountryKnowledgeBase | null {
    // Direct match
    if (COUNTRY_KNOWLEDGE_BASES[country]) {
        return COUNTRY_KNOWLEDGE_BASES[country];
    }
    
    // Fuzzy match
    const normalized = country.toLowerCase();
    for (const [key, value] of Object.entries(COUNTRY_KNOWLEDGE_BASES)) {
        if (key.toLowerCase() === normalized || 
            value.countryCode.toLowerCase() === normalized) {
            return value;
        }
    }
    
    // Default to Philippines if no match
    return PHILIPPINES_KNOWLEDGE;
}

/**
 * Get system prompt addition for a country
 */
export function getCountrySystemPrompt(country: string): string {
    const kb = getCountryKnowledgeBase(country);
    if (!kb) return '';
    return kb.systemPromptAddition;
}

/**
 * Get tax information summary for a country
 */
export function getTaxSummary(country: string): string {
    const kb = getCountryKnowledgeBase(country);
    if (!kb) return '';
    
    const tax = kb.taxation;
    return `
**${kb.country} Tax Overview:**
- Corporate Tax: ${tax.corporateIncomeTax.standardRate}
- VAT/Sales Tax: ${tax.valueAddedTax.standardRate}
- Tax Authority: ${tax.authority}
- Key Laws: ${tax.keyLaws.slice(0, 3).join(', ')}
`;
}

/**
 * Get accounting standards for a country
 */
export function getAccountingStandards(country: string): string {
    const kb = getCountryKnowledgeBase(country);
    if (!kb) return '';
    
    return `
**${kb.country} Accounting Standards:**
- Framework: ${kb.accounting.framework}
- Regulator: ${kb.accounting.regulatoryBody}
- Revenue: ${kb.accounting.keyStandards.revenue}
`;
}

export default {
    COUNTRY_KNOWLEDGE_BASES,
    PHILIPPINES_KNOWLEDGE,
    USA_KNOWLEDGE,
    UK_KNOWLEDGE,
    getCountryKnowledgeBase,
    getCountrySystemPrompt,
    getTaxSummary,
    getAccountingStandards,
};

